import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Loader2, Save } from "lucide-react";
import { Button, Input, Label, Switch } from "@cieda/djayma-ui";

import { ADMIN_THEME } from "@/lib/admin-theme";
import { useConfig, useUpdateConfig } from "@/hooks/admin/useConfig";

import { DEFAULT_CONFIG } from "./constants";
import type { SiteConfig } from "./types";

// ---------------------------------------------------------------------------
// Tab 2: Configuration
// ---------------------------------------------------------------------------

export function ConfigurationTab() {
  const { data: configData, isLoading: configLoading } = useConfig();
  const updateConfig = useUpdateConfig();

  // Map API response to local SiteConfig shape
  const apiConfig = useMemo<SiteConfig>(() => {
    if (!configData) return DEFAULT_CONFIG;
    const entries = configData.app_config;
    const get = (key: string) => entries.find((e) => e.key === key)?.value ?? "";
    return {
      siteName: get("site_name") || DEFAULT_CONFIG.siteName,
      contactEmail: get("contact_email") || DEFAULT_CONFIG.contactEmail,
      contactPhone: get("contact_phone") || DEFAULT_CONFIG.contactPhone,
      minOrderAmount: Number(get("min_order_fcfa")) || DEFAULT_CONFIG.minOrderAmount,
      deliveryEnabled: DEFAULT_CONFIG.deliveryEnabled,
      maintenanceMode: get("maintenance_mode") === "true",
      currency: DEFAULT_CONFIG.currency,
      timezone: DEFAULT_CONFIG.timezone,
    };
  }, [configData]);

  const [config, setConfig] = useState<SiteConfig>(DEFAULT_CONFIG);

  // Sync local state once API data arrives
  useEffect(() => {
    if (configData) setConfig(apiConfig);
  }, [apiConfig, configData]);

  const saving = updateConfig.isPending;

  const updateField = useCallback(
    <K extends keyof SiteConfig>(field: K, value: SiteConfig[K]) => {
      setConfig((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const handleSave = useCallback(async () => {
    await updateConfig.mutateAsync({
      app_config: [
        { key: "contact_email", value: config.contactEmail },
        { key: "contact_phone", value: config.contactPhone },
        { key: "min_order_fcfa", value: config.minOrderAmount },
        { key: "maintenance_mode", value: String(config.maintenanceMode) },
      ],
    });
  }, [config, updateConfig]);

  if (configLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-stone-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* General */}
      <div className="rounded-xl border bg-white p-6" style={{ borderColor: "#e7e0d6" }}>
        <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-stone-600">
          Général
        </h3>
        <div className="grid gap-5 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="siteName" className="text-sm text-stone-700">
              Nom du site
            </Label>
            <Input
              id="siteName"
              value={config.siteName}
              onChange={(e) => updateField("siteName", e.target.value)}
              className="border-stone-200 bg-stone-50 focus:border-stone-300"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="contactEmail" className="text-sm text-stone-700">
              Email de contact
            </Label>
            <Input
              id="contactEmail"
              type="email"
              placeholder="contact@djayma.com"
              value={config.contactEmail}
              onChange={(e) => updateField("contactEmail", e.target.value)}
              className="border-stone-200 bg-stone-50 focus:border-stone-300"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="contactPhone" className="text-sm text-stone-700">
              Téléphone de contact
            </Label>
            <Input
              id="contactPhone"
              type="tel"
              placeholder="+221 76 000 00 00"
              value={config.contactPhone}
              onChange={(e) => updateField("contactPhone", e.target.value)}
              className="border-stone-200 bg-stone-50 focus:border-stone-300"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="minOrder" className="text-sm text-stone-700">
              Montant minimum de commande (FCFA)
            </Label>
            <Input
              id="minOrder"
              type="number"
              min={0}
              step={500}
              value={config.minOrderAmount}
              onChange={(e) =>
                updateField("minOrderAmount", parseInt(e.target.value, 10) || 0)
              }
              className="border-stone-200 bg-stone-50 focus:border-stone-300"
            />
          </div>
        </div>
      </div>

      {/* Toggles */}
      <div className="rounded-xl border bg-white p-6" style={{ borderColor: "#e7e0d6" }}>
        <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-stone-600">
          Fonctionnalités
        </h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-stone-800">Livraison</p>
              <p className="text-xs text-stone-500">
                Activer le service de livraison pour les commandes
              </p>
            </div>
            <Switch
              checked={config.deliveryEnabled}
              onCheckedChange={(checked) =>
                updateField("deliveryEnabled", checked)
              }
              className="data-[state=checked]:bg-[#2D5A3D]"
            />
          </div>
          <div
            className="flex items-center justify-between rounded-lg p-3"
            style={{
              background: config.maintenanceMode
                ? "rgba(234,88,12,0.06)"
                : "transparent",
            }}
          >
            <div className="flex items-start gap-3">
              {config.maintenanceMode && (
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" />
              )}
              <div>
                <p className="text-sm font-medium text-stone-800">
                  Mode maintenance
                </p>
                <p className="text-xs text-stone-500">
                  Le site affichera une page de maintenance aux visiteurs
                </p>
                {config.maintenanceMode && (
                  <p className="mt-1 text-xs font-medium text-orange-600">
                    Attention : le site est actuellement inaccessible aux
                    visiteurs.
                  </p>
                )}
              </div>
            </div>
            <Switch
              checked={config.maintenanceMode}
              onCheckedChange={(checked) =>
                updateField("maintenanceMode", checked)
              }
              className="shrink-0 data-[state=checked]:bg-orange-500"
            />
          </div>
        </div>
      </div>

      {/* Regional */}
      <div className="rounded-xl border bg-white p-6" style={{ borderColor: "#e7e0d6" }}>
        <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-stone-600">
          Régional
        </h3>
        <div className="grid gap-5 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="currency" className="text-sm text-stone-700">
              Devise
            </Label>
            <select
              id="currency"
              value={config.currency}
              onChange={(e) => updateField("currency", e.target.value)}
              className="flex h-9 w-full rounded-md border border-stone-200 bg-stone-50 px-3 py-1 text-sm shadow-sm transition-colors focus:border-stone-300 focus:outline-none focus:ring-1 focus:ring-stone-300"
            >
              <option value="FCFA">FCFA (Franc CFA)</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="timezone" className="text-sm text-stone-700">
              Fuseau horaire
            </Label>
            <select
              id="timezone"
              value={config.timezone}
              onChange={(e) => updateField("timezone", e.target.value)}
              className="flex h-9 w-full rounded-md border border-stone-200 bg-stone-50 px-3 py-1 text-sm shadow-sm transition-colors focus:border-stone-300 focus:outline-none focus:ring-1 focus:ring-stone-300"
            >
              <option value="Africa/Dakar">Africa/Dakar (UTC+0)</option>
              <option value="Africa/Abidjan">Africa/Abidjan (UTC+0)</option>
              <option value="Africa/Lagos">Africa/Lagos (UTC+1)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Save button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="gap-2 text-white"
          style={{ background: ADMIN_THEME.accent.green }}
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          {saving ? "Sauvegarde..." : "Sauvegarder"}
        </Button>
      </div>
    </div>
  );
}
